import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    ArrowRight,
    BadgeCheck,
    BookOpen,
    CheckCircle2,
    ChevronDown,
    ChevronUp,
    ClipboardCheck,
    FileText,
    Globe,
    GraduationCap,
    Landmark,
    MapPin,
    ShieldCheck,
    Sparkles,
    Timer,
    Users,
    X,
} from "lucide-react";

/**
 * UniThink — Country-Focused Admission Support
 * React + Tailwind + Framer Motion (aesthetic + fully populated with your copy)
 *
 * Install:
 *   npm i framer-motion lucide-react
 */

const cx = (...c) => c.filter(Boolean).join(" ");

const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    show: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.55, delay: 0.06 * i, ease: [0.22, 1, 0.36, 1] },
    }),
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.98 },
    show: { opacity: 1, scale: 1, transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] } },
    exit: { opacity: 0, scale: 0.98, transition: { duration: 0.2 } },
};

function Pill({ icon: Icon, children }) {
    return (
        <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-medium text-white/80 backdrop-blur">
            {Icon ? <Icon className="h-4 w-4 text-white/80" /> : null}
            {children}
        </span>
    );
}

function Card({ className, children }) {
    return (
        <div
            className={cx(
                "rounded-2xl border border-white/10 bg-white/5 shadow-[0_10px_30px_-20px_rgba(0,0,0,0.7)]",
                "backdrop-blur",
                className
            )}
        >
            {children}
        </div>
    );
}

function SoftCard({ className, children }) {
    return (
        <div
            className={cx(
                "rounded-2xl border border-slate-200 bg-white shadow-sm",
                "transition hover:shadow-md",
                className
            )}
        >
            {children}
        </div>
    );
}

function SectionTitle({ kicker, title, subtitle, dark }) {
    return (
        <div className={cx("mx-auto max-w-3xl text-center", dark ? "text-white" : "text-slate-900")}>
            {kicker ? (
                <div
                    className={cx(
                        "mb-3 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold",
                        dark
                            ? "border border-white/10 bg-white/5 text-white/80"
                            : "border border-slate-200 bg-slate-50 text-slate-700"
                    )}
                >
                    <Sparkles className="h-3.5 w-3.5" />
                    {kicker}
                </div>
            ) : null}
            <h2
                className={cx(
                    "text-balance text-2xl font-semibold tracking-tight sm:text-3xl",
                    dark ? "text-white" : "text-slate-900"
                )}
            >
                {title}
            </h2>
            {subtitle ? (
                <p className={cx("mt-3 text-pretty", dark ? "text-white/70" : "text-slate-600")}>
                    {subtitle}
                </p>
            ) : null}
        </div>
    );
}

function FAQItem({ q, a, open, onToggle }) {
    return (
        <SoftCard className="overflow-hidden">
            <button onClick={onToggle} className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left">
                <span className="font-medium text-slate-900">{q}</span>
                {open ? <ChevronUp className="h-5 w-5 text-slate-600" /> : <ChevronDown className="h-5 w-5 text-slate-600" />}
            </button>
            <AnimatePresence initial={false}>
                {open ? (
                    <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25 }}
                        className="border-t border-slate-200"
                    >
                        <div className="px-5 py-4 text-sm leading-relaxed text-slate-600">{a}</div>
                    </motion.div>
                ) : null}
            </AnimatePresence>
        </SoftCard>
    );
}

function Modal({ open, onClose, title, children }) {
    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                >
                    <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm" onClick={onClose} />
                    <motion.div
                        variants={scaleIn}
                        initial="hidden"
                        animate="show"
                        exit="exit"
                        className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl"
                        role="dialog"
                        aria-modal="true"
                        aria-label={title}
                    >
                        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                            <div className="font-semibold text-slate-900">{title}</div>
                            <button
                                onClick={onClose}
                                className="rounded-xl border border-slate-200 bg-white p-2 text-slate-700 transition hover:bg-slate-50"
                                aria-label="Close"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="px-5 py-5">{children}</div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

function Toast({ show, message, onClose }) {
    return (
        <AnimatePresence>
            {show ? (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    transition={{ duration: 0.25 }}
                    className="fixed bottom-4 left-1/2 z-[60] w-[min(560px,calc(100%-2rem))] -translate-x-1/2"
                >
                    <div className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-lg">
                        <div className="flex items-start gap-3">
                            <div className="mt-0.5 rounded-xl bg-slate-900 p-2 text-white">
                                <CheckCircle2 className="h-5 w-5" />
                            </div>
                            <div>
                                <div className="font-medium text-slate-900">Submitted.</div>
                                <div className="text-sm text-slate-600">{message}</div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50"
                        >
                            Close
                        </button>
                    </div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

export default function UniThinkCountryFocusedAdmissionSupport() {
    const [openModal, setOpenModal] = useState(false);
    const [faqOpen, setFaqOpen] = useState(0);
    const [toast, setToast] = useState(false);

    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [country, setCountry] = useState("Germany");
    const [level, setLevel] = useState("UG");
    const [intake, setIntake] = useState("");
    const [notes, setNotes] = useState("");
    const [amount, setAmount] = useState("");

    const handleEnquiry = () => {
        if (!amount || isNaN(amount) || amount <= 0) {
            alert("Please enter a valid amount.");
            return;
        }

        const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY,
            amount: parseInt(amount) * 100, // Converts INR to paise
            currency: "INR",
            name: "UniThink",
            description: "Country-Focused Admission Support",
            handler: function (response) {
                // Send data to Google Sheets after successful payment
                fetch("https://script.google.com/macros/s/AKfycbzDt6a4bOBpqeeQtSb5wfEn6D0fHi9ciR_LbwlyPB5A_PUORsg1dK3M5wLAT09ZnHm9hg/exec", {
                    method: "POST",
                    mode: "no-cors",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        name: name,
                        phone: phone,
                        country: country,
                        level: level,
                        intake: intake,
                        notes: notes,
                        payment_status: "Paid",
                        payment_id: response.razorpay_payment_id,
                        service: "Country-Focused Admission Support",
                        amount: amount
                    }),
                }).finally(() => {
                    alert("Payment Successful! We have received your request.");
                    window.location.href = "/";
                });
            },
            prefill: {
                name: name,
                contact: phone,
            },
            theme: { color: "#0f172a" },
            modal: {
                ondismiss: function () {
                    fetch("https://script.google.com/macros/s/AKfycbzDt6a4bOBpqeeQtSb5wfEn6D0fHi9ciR_LbwlyPB5A_PUORsg1dK3M5wLAT09ZnHm9hg/exec", {
                        method: "POST",
                        mode: "no-cors",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({
                            name: name,
                            phone: phone,
                            country: country,
                            level: level,
                            intake: intake,
                            notes: notes,
                            payment_status: "Abandoned",
                            payment_id: "N/A",
                            service: "Country-Focused Admission Support",
                            amount: amount
                        }),
                    });
                }
            }
        };

        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', function (response) {
            fetch("https://script.google.com/macros/s/AKfycbzDt6a4bOBpqeeQtSb5wfEn6D0fHi9ciR_LbwlyPB5A_PUORsg1dK3M5wLAT09ZnHm9hg/exec", {
                method: "POST",
                mode: "no-cors",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: name,
                    phone: phone,
                    country: country,
                    level: level,
                    intake: intake,
                    notes: notes,
                    payment_status: "Payment Failed",
                    payment_id: response.error.metadata.payment_id || "N/A",
                    service: "Country-Focused Admission Support",
                    amount: amount
                }),
            });
        });
        rzp.open();
    };

    const faqs = useMemo(
        () => [
            {
                q: "Why is country-specific support different from normal application support?",
                a: "Country rules can differ in documentation, portals, and consular procedures; errors here are costly and avoidable.",
            },
            {
                q: "Do you guarantee visa approval?",
                a: "No. Visa decisions are made by authorities; UniThink supports compliance and preparedness.",
            },
            {
                q: "Do you support both public and private universities?",
                a: "Scope can be defined based on destination and program type.",
            },
            {
                q: "Is Germany APS mandatory?",
                a: "Official German mission guidance introduced APS as mandatory for student visa applications for Indian students, and requires it before booking visa appointments.",
            },
            {
                q: "What financial proof is typically required for Germany?",
                a: "The student visa checklist for India mentions blocked account proof (11,904 euros for the first year) and a monthly withdrawal cap of 992 euros (from 01 September 2024).",
            },
            {
                q: "Is pre-enrolment required for Italy?",
                a: "The Embassy of Italy in New Delhi describes pre-enrolment as part of the process prior to filing for a study visa, with procedures channelled through Universitaly for certain degree pathways.",
            },
            {
                q: "Do you provide scholarship support?",
                a: "Guidance is provided where applicable, based on destination and eligibility.",
            },
            {
                q: "What is included and excluded financially?",
                a: "University fees, embassy fees, visa fees, courier charges, and third-party charges are excluded unless explicitly stated.",
            },
        ],
        []
    );

    const scrollTo = (id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <div className="relative min-h-screen bg-slate-950 text-white overflow-hidden">
            {/* Background */}
            <div className="pointer-events-none absolute inset-0 -z-10">
                <div className="absolute -top-24 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.35),transparent_60%)] blur-2xl" />
                <div className="absolute -bottom-28 right-[-8rem] h-[520px] w-[520px] rounded-full bg-[radial-gradient(circle_at_center,rgba(236,72,153,0.22),transparent_60%)] blur-2xl" />
                <div className="absolute left-[-10rem] top-1/2 h-[560px] w-[560px] -translate-y-1/2 rounded-full bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.18),transparent_60%)] blur-2xl" />
                <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(2,6,23,0.9),rgba(2,6,23,0.98))]" />
                <div className="absolute inset-0 opacity-[0.25] [background-image:radial-gradient(rgba(255,255,255,0.12)_1px,transparent_1px)] [background-size:22px_22px]" />
            </div>

            {/* Header */}
            <header className="sticky top-0 z-40 border-b border-white/10 bg-slate-950/70 backdrop-blur">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">


                    <nav className="hidden items-center gap-2 md:flex">
                        {[
                            ["included", "Included"],
                            ["examples", "Italy/Germany"],
                            ["how", "How it works"],
                            ["faqs", "FAQs"],
                        ].map(([id, label]) => (
                            <button
                                key={id}
                                onClick={() => scrollTo(id)}
                                className="rounded-xl px-3 py-2 text-sm text-white/80 transition hover:bg-white/5 hover:text-white"
                            >
                                {label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => scrollTo("pricing")}
                            className="hidden rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm text-white/80 transition hover:bg-white/10 sm:inline-flex"
                        >
                            Pricing
                        </button>
                        <button
                            onClick={() => setOpenModal(true)}
                            className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:bg-indigo-400"
                        >
                            Enquire Now <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </header>

            {/* Hero */}
            <section className="relative">
                <div className="mx-auto max-w-6xl px-4 pb-10 pt-12 sm:px-6 sm:pb-14 sm:pt-16">
                    <div className="grid items-start gap-10 lg:grid-cols-[1.15fr_0.85fr]">
                        <div>
                            <motion.div variants={fadeUp} initial="hidden" animate={fadeUp.show(0)}>
                                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-white/80">
                                    <BadgeCheck className="h-4 w-4" /> Destination-specific compliance
                                </div>
                            </motion.div>

                            <motion.h1
                                variants={fadeUp}
                                initial="hidden"
                                animate={fadeUp.show(1)}
                                className="mt-4 text-balance text-3xl font-semibold tracking-tight text-white sm:text-4xl"
                            >
                                Country-Focused Admission Support
                            </motion.h1>

                            <motion.p
                                variants={fadeUp}
                                initial="hidden"
                                animate={fadeUp.show(2)}
                                className="mt-4 text-pretty text-white/70"
                            >
                                Tailored support for destination-specific admissions and compliance—shortlisting, applications, documentation, and visa
                                guidance aligned to country requirements.
                            </motion.p>

                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                animate={fadeUp.show(3)}
                                className="mt-6 flex flex-wrap gap-2"
                            >
                                <Pill icon={Globe}>Country + course shortlisting</Pill>
                                <Pill icon={FileText}>Country-aligned documentation</Pill>
                                <Pill icon={ShieldCheck}>Visa checklist planning</Pill>
                                <Pill icon={GraduationCap}>Scholarship guidance (where applicable)</Pill>
                                <Pill icon={Landmark}>Italy & Germany ready</Pill>
                            </motion.div>

                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                animate={fadeUp.show(4)}
                                className="mt-7 flex flex-col gap-3 sm:flex-row sm:items-center"
                            >
                                <button
                                    onClick={() => setOpenModal(true)}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-500 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:bg-indigo-400"
                                >
                                    Enquire Now <ArrowRight className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => scrollTo("included")}
                                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-3 font-semibold text-white/85 transition hover:bg-white/10"
                                >
                                    What’s Included <ChevronDown className="h-5 w-5" />
                                </button>
                                <div className="text-sm text-white/60 sm:ml-2">
                                    Pricing: <span className="font-semibold text-white/85">Custom</span> (varies by country + scope)
                                </div>
                            </motion.div>

                            <motion.div
                                variants={fadeUp}
                                initial="hidden"
                                animate={fadeUp.show(5)}
                                className="mt-7 rounded-2xl border border-white/10 bg-white/5 p-5"
                            >
                                <div className="flex items-start gap-3">
                                    <div className="mt-0.5 rounded-xl bg-white/10 p-2 ring-1 ring-white/10">
                                        <Sparkles className="h-5 w-5" />
                                    </div>
                                    <div>
                                        <div className="font-semibold text-white">What you get</div>
                                        <p className="mt-1 text-sm text-white/70">
                                            A country-focused plan with the right sequence: shortlisting → documentation → applications → visa readiness.
                                        </p>
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        {/* Context panel */}
                        <motion.div variants={fadeUp} initial="hidden" animate={fadeUp.show(2)} className="lg:pt-3">
                            <Card className="overflow-hidden">
                                <div className="border-b border-white/10 bg-white/5 px-5 py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="font-semibold">Research-backed context</div>
                                        <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1 text-xs font-semibold text-white/70 ring-1 ring-white/10">
                                            <ShieldCheck className="h-3.5 w-3.5" /> Compliance-first
                                        </span>
                                    </div>
                                    <p className="mt-1 text-sm text-white/70">
                                        Global mobility is rising—and country processes are increasingly rule-driven.
                                    </p>
                                </div>

                                <div className="space-y-4 px-5 py-5 text-sm text-white/75">
                                    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                        <div className="font-semibold text-white">UNESCO scale</div>
                                        <p className="mt-1 text-white/70">
                                            UNESCO reports 6.9 million students study outside their home country (tripled since 2000)—higher mobility,
                                            higher admin complexity.
                                        </p>
                                    </div>

                                    <div className="grid gap-3">
                                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                            <div className="font-semibold text-white">Italy: Universitaly pre-enrolment</div>
                                            <p className="mt-1 text-white/70">
                                                Embassy guidance notes pre-enrolment/enrolment procedures before filing a study visa; for certain Laurea/
                                                Laurea Magistrale courses, pre-enrolment is channelled via the UNIVERSITALY portal.
                                            </p>
                                        </div>

                                        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                            <div className="font-semibold text-white">Germany: APS + finance proof</div>
                                            <p className="mt-1 text-white/70">
                                                APS is mandatory for Indian students (introduced Nov 1, 2022) and required before booking a visa
                                                appointment. The visa checklist includes blocked account proof (€11,904 for first year) with a withdrawal cap
                                                of €992/month (as applicable from 01 Sep 2024), plus health insurance expectations.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        <Pill icon={Timer}>Timelines matter</Pill>
                                        <Pill icon={ClipboardCheck}>Checklist discipline</Pill>
                                        <Pill icon={Users}>Parent clarity</Pill>
                                    </div>
                                </div>
                            </Card>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* Included */}
            <section id="included" className="border-t border-white/10 bg-slate-950">
                <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                    <SectionTitle
                        dark
                        kicker="What is included"
                        title="Everything that’s country-specific (and easy to mess up)"
                        subtitle="We focus on rules, documents, and sequencing so you don’t lose time in avoidable errors."
                    />

                    <div className="mt-10 grid gap-6 lg:grid-cols-3">
                        {[
                            {
                                icon: Globe,
                                title: "Destination-specific shortlisting guidance",
                                desc: "University/course prioritisation that matches your eligibility + documentation reality.",
                            },
                            {
                                icon: FileText,
                                title: "Country-aligned application + documentation planning",
                                desc: "Document checklist and portal/process alignment based on destination rules.",
                            },
                            {
                                icon: ShieldCheck,
                                title: "Visa guidance + compliance checklist support",
                                desc: "Readiness planning to reduce documentation gaps and visa mistakes.",
                            },
                            {
                                icon: GraduationCap,
                                title: "Scholarship guidance (where applicable)",
                                desc: "Guidance where available and relevant to destination + profile.",
                            },
                            {
                                icon: Timer,
                                title: "Timeline planning aligned to consular/institution rules",
                                desc: "Sequence steps so you don’t get stuck (APS, pre-enrolment, appointment windows).",
                            },
                            {
                                icon: ClipboardCheck,
                                title: "Tracking + clarity for student and parents",
                                desc: "A structured plan that keeps decisions consistent across steps.",
                            },
                        ].map((it, idx) => (
                            <motion.div
                                key={it.title}
                                variants={fadeUp}
                                initial="hidden"
                                whileInView={fadeUp.show(idx)}
                                viewport={{ once: true, amount: 0.25 }}
                            >
                                <Card className="p-6">
                                    <div className="flex items-start gap-3">
                                        <div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/10">
                                            <it.icon className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="font-semibold text-white">{it.title}</div>
                                            <p className="mt-1 text-sm text-white/70">{it.desc}</p>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <button
                            onClick={() => setOpenModal(true)}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-indigo-500 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:bg-indigo-400"
                        >
                            Enquire Now <ArrowRight className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => scrollTo("examples")}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 px-5 py-3 font-semibold text-white/85 transition hover:bg-white/10"
                        >
                            See Italy & Germany examples <ChevronDown className="h-5 w-5" />
                        </button>
                    </div>
                </div>
            </section>

            {/* Examples */}
            <section id="examples" className="border-t border-white/10 bg-slate-950">
                <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                    <SectionTitle
                        dark
                        kicker="Country examples"
                        title="Italy and Germany: where the checklist makes or breaks timelines"
                        subtitle="Examples of what we align to (your actual scope depends on country + intake)."
                    />

                    <div className="mt-10 grid gap-6 lg:grid-cols-2">
                        <Card className="p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-sm font-semibold text-white/70">Example destination</div>
                                    <div className="mt-1 text-xl font-semibold text-white">🇮🇹 Italy-focused support</div>
                                </div>
                                <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1 text-xs font-semibold text-white/70 ring-1 ring-white/10">
                                    <Landmark className="h-3.5 w-3.5" /> Universitaly
                                </span>
                            </div>

                            <div className="mt-5 space-y-2 text-sm text-white/75">
                                {[
                                    "Pre-enrolment pathway alignment (Universitaly)",
                                    "Documentation planning",
                                    "Visa guidance",
                                    "Timeline sequencing around institutional + consular steps",
                                ].map((x) => (
                                    <div key={x} className="flex gap-2">
                                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-white/85" />
                                        <span>{x}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <Card className="p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-sm font-semibold text-white/70">Example destination</div>
                                    <div className="mt-1 text-xl font-semibold text-white">🇩🇪 Germany-focused support</div>
                                </div>
                                <span className="inline-flex items-center gap-1 rounded-full bg-white/5 px-2 py-1 text-xs font-semibold text-white/70 ring-1 ring-white/10">
                                    <ShieldCheck className="h-3.5 w-3.5" /> APS + Finance
                                </span>
                            </div>

                            <div className="mt-5 space-y-2 text-sm text-white/75">
                                {[
                                    "APS guidance",
                                    "Proof-of-finance planning (blocked account requirement)",
                                    "Visa documentation planning",
                                    "Health insurance expectations",
                                ].map((x) => (
                                    <div key={x} className="flex gap-2">
                                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-white/85" />
                                        <span>{x}</span>
                                    </div>
                                ))}
                            </div>


                        </Card>
                    </div>
                </div>
            </section>

            {/* How it works */}
            <section id="how" className="border-t border-white/10 bg-slate-950">
                <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                    <SectionTitle
                        dark
                        kicker="How it works"
                        title="A strict process needs a strict sequence"
                        subtitle="Four steps that keep you compliant and on time."
                    />

                    <div className="mt-10 grid gap-6 lg:grid-cols-4">
                        {[
                            {
                                icon: ClipboardCheck,
                                title: "Step 1: Destination intake",
                                desc: "Country, program type, intake timeline, eligibility.",
                            },
                            {
                                icon: Globe,
                                title: "Step 2: Checklist + shortlist",
                                desc: "Aligned to destination rules and documentation.",
                            },
                            {
                                icon: FileText,
                                title: "Step 3: Application execution guidance",
                                desc: "Build, review, track, and follow up professionally.",
                            },
                            {
                                icon: ShieldCheck,
                                title: "Step 4: Visa readiness checks",
                                desc: "Submission readiness, formatting, sequencing, and gaps.",
                            },
                        ].map((it, idx) => (
                            <motion.div
                                key={it.title}
                                variants={fadeUp}
                                initial="hidden"
                                whileInView={fadeUp.show(idx)}
                                viewport={{ once: true, amount: 0.25 }}
                            >
                                <Card className="p-6">
                                    <div className="flex items-start gap-3">
                                        <div className="rounded-2xl bg-white/10 p-3 ring-1 ring-white/10">
                                            <it.icon className="h-5 w-5" />
                                        </div>
                                        <div>
                                            <div className="font-semibold text-white">{it.title}</div>
                                            <p className="mt-1 text-sm text-white/70">{it.desc}</p>
                                        </div>
                                    </div>
                                </Card>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-10 grid gap-6 lg:grid-cols-2">
                        <Card className="p-6">
                            <div className="flex items-center gap-2 text-sm font-semibold text-white">
                                <CheckCircle2 className="h-4 w-4" /> Outcomes you can expect
                            </div>
                            <div className="mt-4 space-y-2 text-sm text-white/75">
                                {[
                                    "Reduced risk of documentation gaps or visa readiness mistakes",
                                    "A clearer sequence of steps aligned to country process requirements",
                                    "Stronger end-to-end clarity for student and parent decision-making",
                                    "Better time management across pre-enrolment, applications, and visa steps",
                                ].map((x) => (
                                    <div key={x} className="flex gap-2">
                                        <CheckCircle2 className="mt-0.5 h-4 w-4 flex-none text-white/85" />
                                        <span>{x}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>

                        <Card className="p-6">
                            <div className="flex items-center gap-2 text-sm font-semibold text-white">
                                <BookOpen className="h-4 w-4" /> Recommended reading
                            </div>
                            <div className="mt-4 grid gap-3">
                                {[
                                    "Germany student visa readiness: APS, finances, and timelines",
                                    "Italy admissions and pre-enrolment: how to avoid avoidable delays",
                                    "What changes between public and private university processes across destinations",
                                ].map((x) => (
                                    <div key={x} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-white/80 transition hover:bg-white/10">
                                        <div className="font-semibold text-white">{x}</div>
                                        <div className="mt-1 text-xs text-white/60">Comming soon</div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section id="pricing" className="border-t border-white/10 bg-slate-950">
                <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                    <SectionTitle
                        dark
                        kicker="Pricing"
                        title="Custom Pricing (varies by country, university type, and scope)"
                        subtitle="We confirm scope first, then pricing. No surprises mid-way."
                    />

                    <div className="mt-10 mx-auto max-w-2xl">
                        <Card className="p-6">
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                <div>
                                    <div className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1 text-xs font-semibold text-white/70 ring-1 ring-white/10">
                                        <BadgeCheck className="h-4 w-4" /> Scope-first pricing
                                    </div>
                                    <div className="mt-4 text-2xl font-semibold text-white">Enquire → Confirm scope → Start</div>
                                    <p className="mt-2 text-sm text-white/70">
                                        Country rules differ. Your intake, program type, and university type changes what’s required.
                                    </p>
                                </div>

                                <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                    <div className="text-xs font-semibold text-white/60">Best for</div>
                                    <div className="mt-1 text-sm font-semibold text-white">
                                        Italy / Germany & other rule-heavy destinations
                                    </div>
                                </div>
                            </div>

                            <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                {[
                                    { label: "Country", hint: "e.g., Germany" },
                                    { label: "Program level", hint: "UG / PG" },
                                    { label: "Intake", hint: "Sep 2026" },
                                    { label: "University type", hint: "Public / Private" },
                                ].map((f) => (
                                    <div key={f.label} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                                        <div className="text-xs font-semibold text-white/60">{f.label}</div>
                                        <div className="mt-1 text-sm font-semibold text-white/85">{f.hint}</div>
                                    </div>
                                ))}
                            </div>

                            <button
                                onClick={() => setOpenModal(true)}
                                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-500 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:bg-indigo-400"
                            >
                                Enquire Now <ArrowRight className="h-5 w-5" />
                            </button>

                            <p className="mt-3 text-xs text-white/60">
                                Note: University fees, embassy fees, visa fees, courier charges, and third-party charges are excluded unless explicitly stated.
                            </p>
                        </Card>


                    </div>
                </div>
            </section>

            {/* FAQs */}
            <section id="faqs" className="border-t border-white/10 bg-white">
                <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16">
                    <SectionTitle
                        kicker="FAQs"
                        title="Quick answers (so your family stays aligned)"
                        subtitle="Country rules can be strict — clarity up front saves weeks later."
                    />

                    <div className="mt-10 grid gap-4 lg:grid-cols-2">
                        {faqs.map((f, i) => (
                            <FAQItem
                                key={f.q}
                                q={f.q}
                                a={f.a}
                                open={faqOpen === i}
                                onToggle={() => setFaqOpen((p) => (p === i ? -1 : i))}
                            />
                        ))}
                    </div>

                    <div className="mt-10 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                            <div>
                                <div className="text-lg font-semibold text-slate-900">
                                    For destinations where rules are strict, the right checklist and sequence are everything.
                                </div>
                                <div className="mt-1 text-sm text-slate-600">
                                    Enquire now to get a country-focused plan and confirmed scope.
                                </div>
                            </div>
                            <button
                                onClick={() => setOpenModal(true)}
                                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-900 px-5 py-3 font-semibold text-white shadow-sm transition hover:translate-y-[-1px] hover:shadow-md"
                            >
                                Enquire Now <ArrowRight className="h-5 w-5" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>



            {/* Modal */}
            <Modal open={openModal} onClose={() => setOpenModal(false)} title="Enquire Now — Country-Focused Support">
                <div className="grid gap-4 sm:grid-cols-2">
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Student Name</span>
                        <input
                            value={name} onChange={(e) => setName(e.target.value)}
                            className="rounded-xl text-black border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., Arjun"
                        />
                    </label>
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Phone Number</span>
                        <input
                            value={phone} onChange={(e) => setPhone(e.target.value)}
                            className="rounded-xl text-black border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., +91 XXXXX XXXXX"
                        />
                    </label>
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Destination country</span>
                        <select value={country} onChange={(e) => setCountry(e.target.value)} className="rounded-xl border text-black border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400">
                            <option>Germany</option>
                            <option>Italy</option>
                            <option>Other</option>
                        </select>
                    </label>
                    <label className="grid gap-1">
                        <span className="text-xs font-medium text-slate-700">Program level</span>
                        <select value={level} onChange={(e) => setLevel(e.target.value)} className="rounded-xl text-black border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400">
                            <option>UG</option>
                            <option>PG</option>
                            <option>PhD</option>
                        </select>
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Intake timeline</span>
                        <input
                            value={intake} onChange={(e) => setIntake(e.target.value)}
                            className="rounded-xl border text-black border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="e.g., Sep 2026 / Winter 2026"
                        />
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Notes</span>
                        <textarea
                            value={notes} onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="rounded-xl text-black border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="Program interests, public/private preference, budget constraints, current education, etc."
                        />
                    </label>
                    <label className="grid gap-1 sm:col-span-2">
                        <span className="text-xs font-medium text-slate-700">Payment Amount (INR)</span>
                        <input
                            type="number"
                            value={amount} onChange={(e) => setAmount(e.target.value)}
                            className="rounded-xl text-black border border-slate-200 bg-white px-3 py-2 text-sm outline-none transition focus:border-slate-400"
                            placeholder="Enter amount"
                            min="1"
                        />
                    </label>
                </div>

                <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-xs text-slate-500">Next: connect this form to WhatsApp / Google Sheets / CRM.</div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setOpenModal(false)}
                            className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition hover:bg-slate-50"
                        >
                            Not now
                        </button>
                        <button
                            onClick={handleEnquiry}
                            className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:translate-y-[-1px]"
                        >
                            Submit enquiry <ArrowRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </Modal>

            <Toast
                show={toast}
                message="We captured your enquiry. Our team will contact you soon."
                onClose={() => setToast(false)}
            />
        </div>
    );
}
